import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePersistedState } from "@/hooks/usePersistedState";
import { useApi } from "@/hooks/useApi";
import { useScanQuota } from "@/hooks/useScanQuota";
import { apiClient } from "@/services/api";
import type { UserData } from "@/types/user";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  ArrowRight,
  Plus,
  Trash2,
  Settings,
  Clock,
  Loader2,
  Info,
  AlertCircle,
  X,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Collapsible } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PolicyConfiguration } from "@/components/PolicyConfiguration";
import type {
  AgentType,
  HttpMethod,
  HttpHeader,
  RedteamConfig,
  FoundationModelId,
  PolicyObject,
  PluginConfiguration,
} from "@/types/redteam";
import { FOUNDATION_MODELS, HTTP_METHODS, REDTEAM_DEFAULTS } from "@/constants/redteam";
import {
  PLUGIN_PRESETS,
  getAllPlugins,
  getPluginDisplayName,
  getPluginDescription,
  pluginRequiresConfiguration,
  getPluginConfigSchema,
} from "@/constants/plugins";
import { STRATEGY_PRESETS, STRATEGIES } from "@/constants/strategies";
import type { Strategy } from "@/types/redteam";
import {
  createDefaultRedteamConfig,
  updateConfigTarget,
  updateConfigPrompts,
  updateConfigPlugins,
  updateConfigStrategies,
  prepareConfigForSubmission,
} from "@/utils/redteamConfig";
import type { CloneConfigResponse } from "@/types/llm";
import { generateYaml } from "@/utils/yamlGenerator";
import {
  listUserProviderConfigs,
  listManagedProviderSecrets,
  extractProviderFromModelId,
  resolveCredentialsForProvider,
  createProviderConfig,
  findExistingProviderConfig,
  requiresApiKey,
  hasCredentialsForProvider,
} from "@/services/providerConfigService";
import { createScan } from "@/services/llmScanService";
import { getCloneConfig } from "@/services/shareService";
import { ApiError } from "@/services/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { LLMProviderConfigRead, LLMManagedProviderSecretRead } from "@/types/provider";
import type { LLMScanCreate } from "@/types/llm";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useScanWizardTour } from "@/hooks/useScanWizardTour";
import { HelpCircle } from "lucide-react";

import type { Plugin } from "@/types/redteam";

// Hardcoded strategy presets - will be moved to constants later
// Strategy types and constants are now imported from @/constants/strategies

// Get all unique plugins from all presets for display (sorted alphabetically)
const ALL_PLUGINS = getAllPlugins();

// Model options now come from FOUNDATION_MODELS constant

export function DemoFlowPage() {
  useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const { track } = useAnalytics();

  // Fetch current user to get username
  const { data: userData } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await apiClient.get<UserData>("/api/v1/user/me/");
      return data;
    },
    staleTime: 30000, // 30 seconds
  });

  // Fetch scan quota
  const {
    data: quota,
    isLoading: quotaLoading,
    refetch: refetchQuota,
  } = useScanQuota(userData?.username);
  const activeScans = quota?.active_scans ?? 0;
  // Use available_reports directly from backend (already accounts for used_reports + active_scans)
  // Backend calculates: available_reports = max(0, max_reports - used_reports - active_scans)
  const availableReports = quota && !quota.is_unlimited ? (quota.available_reports ?? 0) : null;
  const isQuotaExceeded = quota && !quota.is_unlimited && (availableReports ?? 0) <= 0;
  // Active scans no longer block scan creation as long as the user still has available report capacity.

  // ============================================================================
  // STATE MANAGEMENT EXPLANATION:
  // ============================================================================
  // We use TWO levels of state:
  //
  // 1. UI STATE (Form inputs):
  //    - agentType, apiEndpoint, method, headers, requestBody, etc.
  //    - These are the individual form fields the user fills in
  //    - They exist only in the component's local state
  //
  // 2. CONFIG STATE (Accumulated Redteam Config):
  //    - redteamConfig: The final config object that will become YAML
  //    - This accumulates data as the user progresses through steps
  //    - When user completes Step 2, we convert UI state → config state
  //    - This config follows the promptfoo format and will be used for YAML generation
  //
  // FLOW:
  // Step 1: User selects agent type → stored in UI state only
  // Step 2: User fills form → stored in UI state
  //         → On "Next", handleStep2Complete() converts UI state to config
  //         → Config now has: target (ProviderOptions), prompts
  // Step 3: User selects plugins → will be added to config.plugins
  // Step 4: User selects strategies → will be added to config.strategies
  // Step 5: Final config is ready → can generate YAML
  // ============================================================================

  // Redteam config - accumulates data as user progresses through steps
  // This is the final config object that matches promptfoo format
  const [redteamConfig, setRedteamConfig, clearRedteamConfig] = usePersistedState<RedteamConfig>(
    "demo-flow-config",
    createDefaultRedteamConfig()
  );

  // Memoize YAML generation to avoid re-running on every render
  const generatedYaml = useMemo(
    () => (process.env.NODE_ENV === "development" ? generateYaml(redteamConfig) : ""),
    [redteamConfig]
  );

  // Navigation
  const navigate = useNavigate();

  // Scan submission state
  const [isSubmittingScan, setIsSubmittingScan] = useState(false);
  const [scanSubmitError, setScanSubmitError] = useState<string | null>(null);
  const [createdScanUuid, setCreatedScanUuid] = useState<string | null>(null);

  // Step tracking
  const [currentStep, setCurrentStep, clearCurrentStep] = usePersistedState<1 | 2 | 3 | 4 | 5>(
    "demo-flow-step",
    1
  );

  // Step 1: Agent selection
  const [agentType, setAgentType, clearAgentType] = usePersistedState<AgentType | "">(
    "demo-flow-agent",
    ""
  );

  // Tour hook - passes current step and agent type for contextual help
  const { restartTour } = useScanWizardTour(currentStep, agentType === "" ? null : agentType);

  // Step 2a: Own agent configuration
  const [apiEndpoint, setApiEndpoint, clearApiEndpoint] = usePersistedState<string>(
    "demo-flow-api-endpoint",
    ""
  );
  const [method, setMethod, clearMethod] = usePersistedState<HttpMethod>(
    "demo-flow-method",
    REDTEAM_DEFAULTS.DEFAULT_METHOD
  );
  // Helper to generate unique IDs for headers
  const generateHeaderId = () => `header-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  const [headers, setHeaders, clearHeaders] = usePersistedState<HttpHeader[]>("demo-flow-headers", [
    { id: generateHeaderId(), name: "", value: "" },
  ]);

  // Ensure all headers have IDs (backward compatibility for headers loaded from localStorage)
  useEffect(() => {
    const needsIds = headers.some((h) => !h.id);
    if (needsIds) {
      setHeaders((prev) =>
        prev.map((header) => (header.id ? header : { ...header, id: generateHeaderId() }))
      );
    }
  }, []); // Only run once on mount

  const [requestBody, setRequestBody, clearRequestBody] = usePersistedState<string>(
    "demo-flow-request-body",
    ""
  );

  // Step 3: Plugin selection and configuration
  const [selectedPreset, setSelectedPreset, clearSelectedPreset] = usePersistedState<string>(
    "demo-flow-plugin-preset",
    ""
  );
  const [selectedPlugins, setSelectedPlugins, clearSelectedPlugins] = usePersistedState<Plugin[]>(
    "demo-flow-plugins",
    []
  );
  const [pluginConfigurations, setPluginConfigurations, clearPluginConfigurations] =
    usePersistedState<Record<Plugin, PluginConfiguration>>("demo-flow-plugin-configs", {});
  const [configuringPluginId, setConfiguringPluginId] = useState<Plugin | null>(null);
  const [tempConfig, setTempConfig] = useState<PluginConfiguration>({});

  // Step 4: Strategy selection
  const [selectedStrategyPreset, setSelectedStrategyPreset, clearSelectedStrategyPreset] =
    usePersistedState<string>("demo-flow-strategy-preset", "");
  const [selectedStrategies, setSelectedStrategies, clearSelectedStrategies] = usePersistedState<
    Strategy[]
  >("demo-flow-strategies", []);

  // Step 2b: Our AI configuration
  const [selectedModel, setSelectedModel, clearSelectedModel] = usePersistedState<string>(
    "demo-flow-model",
    ""
  );
  const [prompt, setPrompt, clearPrompt] = usePersistedState<string>("demo-flow-prompt", "");
  const [temperature, setTemperature, clearTemperature] = usePersistedState<number | "">(
    "demo-flow-temperature",
    ""
  );
  const [maxTokens, setMaxTokens, clearMaxTokens] = usePersistedState<number | "">(
    "demo-flow-max-tokens",
    ""
  );
  const [topP, setTopP, clearTopP] = usePersistedState<number | "">("demo-flow-top-p", "");
  const [apiKey, setApiKey] = useState<string>("");
  const [saveApiKey, setSaveApiKey, clearSaveApiKey] = usePersistedState<boolean>(
    "demo-flow-save-api-key",
    false
  );

  // Provider credentials state
  const [userProviderConfigs, setUserProviderConfigs] = useState<LLMProviderConfigRead[]>([]);
  const [managedSecrets, setManagedSecrets] = useState<LLMManagedProviderSecretRead[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [isCreatingProviderConfig, setIsCreatingProviderConfig] = useState(false);
  const [isPromptInfoExpanded, setIsPromptInfoExpanded] = useState(false);
  const [isHeadersInfoExpanded, setIsHeadersInfoExpanded] = useState(false);
  const [isRequestBodyInfoExpanded, setIsRequestBodyInfoExpanded] = useState(false);
  const [isConfigCloned, setIsConfigCloned] = useState(false);
  const [step2Error, setStep2Error] = useState<string | null>(null);

  // Function to clear all persisted state (call when flow completes or user starts fresh)
  const clearAllPersistedState = () => {
    clearRedteamConfig();
    clearCurrentStep();
    clearAgentType();
    clearApiEndpoint();
    clearMethod();
    clearHeaders();
    clearRequestBody();
    clearSelectedPreset();
    clearSelectedPlugins();
    clearPluginConfigurations();
    clearSelectedStrategyPreset();
    clearSelectedStrategies();
    clearSelectedModel();
    clearPrompt();
    clearTemperature();
    clearMaxTokens();
    clearTopP();
    setApiKey("");
    clearSaveApiKey();
  };

  // Clone scan configuration from shared report (runs once on mount)
  // Reads cloneFrom URL param and fetches config from server
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneLoading, setCloneLoading] = useState(false);

  useEffect(() => {
    const cloneFromToken = searchParams.get("cloneFrom");
    if (!cloneFromToken) {
      return;
    }

    let isMounted = true;

    const applyClonedConfig = (cloneConfig: CloneConfigResponse) => {
      if (!isMounted) return;

      setIsConfigCloned(true);

      // Reset to step 1 so user starts fresh with cloned config
      setCurrentStep(1);

      // Set agent type to "our-ai" (Quick Scan)
      setAgentType("our-ai");

      // Set prompt if available
      if (cloneConfig.prompt) {
        setPrompt(cloneConfig.prompt);
      }

      // Set plugins - filter to only include valid plugin IDs
      if (cloneConfig.plugins && cloneConfig.plugins.length > 0) {
        const validPlugins = cloneConfig.plugins.filter((pluginId) =>
          ALL_PLUGINS.includes(pluginId)
        );
        if (validPlugins.length > 0) {
          setSelectedPlugins(validPlugins);
          const matchedPreset = PLUGIN_PRESETS.find((preset) => {
            if (!preset.available) return false;
            const presetPluginSet = new Set(preset.pluginIds);
            const clonedPluginSet = new Set(validPlugins);
            if (presetPluginSet.size !== clonedPluginSet.size) return false;
            return [...presetPluginSet].every((p) => clonedPluginSet.has(p));
          });

          if (matchedPreset) {
            setSelectedPreset(matchedPreset.id);
          }
        }
      }

      // Set strategies if available
      if (cloneConfig.strategies && cloneConfig.strategies.length > 0) {
        setSelectedStrategies(cloneConfig.strategies);

        const matchedStrategyPreset = STRATEGY_PRESETS.find((preset) => {
          if (!preset.available) return false;
          const allPresetStrategyIds = Object.values(preset.strategyIds || {}).flat();
          const presetStrategySet = new Set(allPresetStrategyIds);
          const clonedStrategySet = new Set(cloneConfig.strategies);
          if (presetStrategySet.size !== clonedStrategySet.size) return false;
          return [...presetStrategySet].every((s) => clonedStrategySet.has(s));
        });

        if (matchedStrategyPreset) {
          setSelectedStrategyPreset(matchedStrategyPreset.id);
        }
      } else {
        setSelectedStrategyPreset("quick");
        setSelectedStrategies(["basic"]);
      }
    };

    const fetchCloneConfig = async () => {
      if (!isMounted) return;

      setCloneLoading(true);
      setCloneError(null);
      try {
        const cloneConfig = await getCloneConfig(cloneFromToken);
        if (!isMounted) return;

        applyClonedConfig(cloneConfig);
        // Clean URL after successfully loading config
        searchParams.delete("cloneFrom");
        setSearchParams(searchParams, { replace: true });
      } catch (error) {
        if (!isMounted) return;

        console.error("Failed to fetch clone config:", error);
        // Handle specific error cases using structured error properties
        if (error instanceof ApiError) {
          if (error.status === 403) {
            setCloneError("Cannot clone password-protected reports");
          } else if (error.status === 401) {
            setCloneError("This report is no longer available");
          } else if (error.status === 404) {
            setCloneError("Report not found");
          } else {
            setCloneError("Failed to load report configuration");
          }
        } else {
          setCloneError("Failed to load report configuration");
        }
        // Clean URL even on error
        searchParams.delete("cloneFrom");
        setSearchParams(searchParams, { replace: true });
      } finally {
        if (isMounted) {
          setCloneLoading(false);
        }
      }
    };

    fetchCloneConfig();

    return () => {
      isMounted = false;
    };
  }, [searchParams, setSearchParams]); // Run when searchParams change

  // Fetch provider credentials when entering Step 2b (Our AI flow)
  useEffect(() => {
    let isMounted = true;

    const fetchCredentials = async () => {
      // Only fetch when we're on Step 2 and user selected "our-ai"
      if (currentStep === 2 && agentType === "our-ai") {
        if (!isMounted) return;

        setCredentialsLoading(true);
        try {
          const [userConfigs, managed] = await Promise.all([
            listUserProviderConfigs(),
            listManagedProviderSecrets(),
          ]);
          if (isMounted) {
            setUserProviderConfigs(userConfigs);
            setManagedSecrets(managed);
          }
        } catch (error) {
          console.error("Failed to fetch provider credentials:", error);
        } finally {
          if (isMounted) {
            setCredentialsLoading(false);
          }
        }
      }
    };

    fetchCredentials();
    return () => {
      isMounted = false;
    };
  }, [currentStep, agentType]);

  const handleFrameworkChange = (frameworkId: string) => {
    const framework = PLUGIN_PRESETS.find((p) => p.id === frameworkId);
    if (!framework) {
      setSelectedPreset("");
      setSelectedPlugins([]);
      return;
    }

    if (!framework.available) {
      alert(
        "This compliance framework requires a paid plan. Please upgrade to access more frameworks."
      );
      return;
    }

    setSelectedPreset(frameworkId);
    setSelectedPlugins(framework.pluginIds);
  };

  const handlePluginToggle = (pluginId: Plugin) => {
    // Only allow manual toggling when no compliance framework is selected
    if (selectedPreset) {
      return;
    }
    setSelectedPlugins((prev) =>
      prev.includes(pluginId) ? prev.filter((id) => id !== pluginId) : [...prev, pluginId]
    );
  };

  const handleStrategyPresetChange = (presetId: string) => {
    const preset = STRATEGY_PRESETS.find((p) => p.id === presetId);
    if (!preset) {
      setSelectedStrategyPreset("");
      setSelectedStrategies([]);
      return;
    }

    if (!preset.available) {
      alert("This preset requires a paid plan. Please upgrade to access more presets.");
      return;
    }

    setSelectedStrategyPreset(presetId);
    // Collect all strategy IDs from the preset and deduplicate
    const allStrategyIds: Strategy[] = [
      ...(preset.strategyIds.recommended || []),
      ...(preset.strategyIds.agenticSingleTurn || []),
      ...(preset.strategyIds.agenticMultiTurn || []),
      // Note: multimodal strategies are not auto-selected by presets
    ];
    // Remove duplicates (e.g., jailbreak:meta appears in both recommended and agentic-single)
    const uniqueStrategyIds = Array.from(new Set(allStrategyIds));
    setSelectedStrategies(uniqueStrategyIds);
  };

  const handleStrategyToggle = (strategyId: Strategy) => {
    const strategy = STRATEGIES.find((s) => s.id === strategyId);
    if (!strategy) return;

    if (strategy.locked && strategy.requiresPayment) {
      alert("This strategy requires a paid plan. Please upgrade to access more strategies.");
      return;
    }

    setSelectedStrategies((prev) =>
      prev.includes(strategyId) ? prev.filter((id) => id !== strategyId) : [...prev, strategyId]
    );
  };

  const handleOpenPluginConfig = (pluginId: Plugin) => {
    setConfiguringPluginId(pluginId);
    setTempConfig(pluginConfigurations[pluginId] || {});
  };

  const handleClosePluginConfig = () => {
    setConfiguringPluginId(null);
    setTempConfig({});
  };

  const handleSavePluginConfig = () => {
    if (!configuringPluginId) return;

    // Clean up empty values (especially empty strings in arrays)
    const cleanedConfig: PluginConfiguration = {};
    const schema = getPluginConfigSchema(configuringPluginId);

    schema.fields.forEach((field) => {
      const value = tempConfig[field.key];
      if (value === undefined || value === null) return;

      if (field.type === "array") {
        // Special handling for policy plugin
        if (configuringPluginId === "policy" && field.key === "policies") {
          const policiesArray = Array.isArray(value) ? (value as PolicyObject[]) : [];
          const validPolicies = policiesArray.filter(
            (p) => p && typeof p === "object" && p.text && p.text.trim() !== ""
          );
          if (validPolicies.length > 0) {
            // Ensure each policy has an id
            cleanedConfig[field.key] = validPolicies.map((p) => ({
              id: p.id || `policy-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`,
              name: p.name || "",
              text: p.text.trim(),
            }));
          }
        } else {
          // Regular string array - handle both normalized (with id/value) and plain string arrays
          const arrayValue = Array.isArray(value) ? value : [];
          let nonEmptyItems: string[] = [];

          if (arrayValue.length > 0) {
            // Check if normalized format (has id and value properties)
            if (
              typeof arrayValue[0] === "object" &&
              arrayValue[0] !== null &&
              "id" in arrayValue[0] &&
              "value" in arrayValue[0]
            ) {
              // Extract values from normalized format
              nonEmptyItems = (arrayValue as unknown as Array<{ id: string; value: string }>)
                .map((item) => item.value)
                .filter((item) => typeof item === "string" && item.trim() !== "");
            } else {
              // Plain string array
              nonEmptyItems = arrayValue.filter(
                (item) => typeof item === "string" && item.trim() !== ""
              ) as string[];
            }
          }

          if (nonEmptyItems.length > 0) {
            cleanedConfig[field.key] = nonEmptyItems;
          }
        }
      } else if (typeof value === "string" && value.trim() !== "") {
        cleanedConfig[field.key] = value.trim();
      } else if (typeof value !== "string") {
        cleanedConfig[field.key] = value;
      }
    });

    setPluginConfigurations((prev) => ({
      ...prev,
      [configuringPluginId]: cleanedConfig,
    }));

    handleClosePluginConfig();
  };

  const isPluginConfigured = (pluginId: Plugin): boolean => {
    // intent and policy plugins are optional - they don't block proceeding if not configured
    if (pluginId === "intent" || pluginId === "policy") {
      return true;
    }

    if (!pluginRequiresConfiguration(pluginId)) return true;

    const schema = getPluginConfigSchema(pluginId);
    const config = pluginConfigurations[pluginId];

    if (!config || Object.keys(config).length === 0) return false;

    // Check that all required fields (except graderGuidance) are filled
    const requiredFields = schema.fields.filter(
      (field) => field.required && field.key !== "graderGuidance"
    );

    return requiredFields.every((field) => {
      const value = config[field.key];
      if (field.type === "array") {
        // Special handling for policy plugin
        if (pluginId === "policy" && field.key === "policies") {
          const policiesArray = Array.isArray(value) ? (value as PolicyObject[]) : [];
          return (
            policiesArray.length > 0 &&
            policiesArray.every((p) => p && typeof p === "object" && p.text && p.text.trim() !== "")
          );
        }
        // Regular string array
        const arrayValue = Array.isArray(value) ? (value as string[]) : [];
        return (
          arrayValue.length > 0 &&
          arrayValue.every((item) => typeof item === "string" && item.trim() !== "")
        );
      }
      return value !== undefined && value !== "" && String(value).trim() !== "";
    });
  };

  const handleAddHeader = () => {
    setHeaders((prev) => [...prev, { id: generateHeaderId(), name: "", value: "" }]);
  };

  const handleRemoveHeader = (id: string) => {
    setHeaders((prev) => prev.filter((header) => header.id !== id));
  };

  const handleHeaderChange = (id: string, field: "name" | "value", newValue: string) => {
    setHeaders((prev) =>
      prev.map((header) => (header.id === id ? { ...header, [field]: newValue } : header))
    );
  };

  const hasPromptPlaceholder = (body: string): boolean => {
    return body.includes("{{prompt}}");
  };

  const isValidUrl = (url: string): boolean => {
    const trimmed = url.trim();
    if (!trimmed) return false;
    // Must start with http:// or https://
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      return false;
    }
    // Try to parse as URL
    try {
      new URL(trimmed);
      return true;
    } catch {
      return false;
    }
  };

  const getUrlError = (url: string): string | null => {
    const trimmed = url.trim();
    if (!trimmed) return null; // Empty is handled by other validation
    if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
      return "URL must start with https:// or http://";
    }
    try {
      new URL(trimmed);
      return null;
    } catch {
      return "Invalid URL format";
    }
  };

  const canProceedFromStep1 = agentType !== "";
  const canProceedFromStep2a =
    agentType === "own" &&
    apiEndpoint.trim() !== "" &&
    isValidUrl(apiEndpoint) &&
    requestBody.trim() !== "" &&
    hasPromptPlaceholder(requestBody);
  const canProceedFromStep2b = useMemo(() => {
    if (agentType !== "our-ai") {
      return false;
    }
    if (selectedModel === "" || prompt.trim() === "") {
      return false;
    }
    const providerType = extractProviderFromModelId(selectedModel);
    if (!providerType) {
      return false;
    }
    const needsApiKey = requiresApiKey(selectedModel);
    const hasCredentials = hasCredentialsForProvider(
      providerType,
      apiKey,
      userProviderConfigs,
      managedSecrets
    );

    // For Anthropic models, we MUST have credentials
    if (needsApiKey && !hasCredentials) {
      return false;
    }

    return true;
  }, [agentType, selectedModel, prompt, apiKey, userProviderConfigs, managedSecrets]);
  const canProceedFromStep3 =
    selectedPlugins.length > 0 && selectedPlugins.every((pluginId) => isPluginConfigured(pluginId));
  const canProceedFromStep4 = selectedStrategies.length > 0;

  // Update config when moving from Step 2
  const handleStep2Complete = async (): Promise<boolean> => {
    try {
      let updatedConfig = redteamConfig;

      if (agentType === "own") {
        // Update config with HTTP target
        updatedConfig = updateConfigTarget(redteamConfig, "own", {
          url: apiEndpoint,
          method,
          headers,
          body: requestBody,
        });
        // Update prompts to use the prompt placeholder
        updatedConfig = updateConfigPrompts(updatedConfig, ["{{prompt}}"]);
      } else if (agentType === "our-ai") {
        // Extract provider type from model ID
        const providerType = extractProviderFromModelId(selectedModel);
        if (!providerType) {
          throw new Error(`Invalid model ID format: ${selectedModel}`);
        }

        // Check inline API key or active saved provider config
        const hasInlineKey = apiKey.trim() !== "";
        const hasSavedConfig = userProviderConfigs.some(
          (config) => config.provider_type === providerType && config.is_active
        );
        if (requiresApiKey(selectedModel) && !hasInlineKey && !hasSavedConfig) {
          throw new Error(
            "API key is required for Anthropic models. Please provide an API key in the Advanced Configuration section or ensure you have a saved provider configuration."
          );
        }

        // Resolve credential source (priority: inline API key > user config > managed secret)
        let credentialResolution = resolveCredentialsForProvider(
          providerType,
          userProviderConfigs,
          managedSecrets,
          apiKey.trim() || undefined
        );

        const shouldSaveKey = saveApiKey;

        // If user provided inline API key and wants to save it, create/update provider config
        if (credentialResolution.source === "inline" && apiKey.trim() && shouldSaveKey) {
          const existingConfig = findExistingProviderConfig(providerType, userProviderConfigs);

          if (!existingConfig) {
            // Create a new provider config to store their API key securely
            try {
              setIsCreatingProviderConfig(true);
              const newConfig = await createProviderConfig({
                provider_type: providerType,
                credentials: { api_key: apiKey.trim() },
              });

              // Update local state with the new config
              setUserProviderConfigs((prev) => [...prev, newConfig]);

              // Update credential resolution to use the new config
              credentialResolution = {
                source: "user",
                configId: newConfig.uuid,
              };
            } catch (createError) {
              console.error("Failed to create provider config:", createError);
              // Fall back to using the inline key for this scan only
              setSaveApiKey(false);
              credentialResolution = { source: "inline" };
            } finally {
              setIsCreatingProviderConfig(false);
            }
          } else {
            // User has an existing config, use it instead of creating a new one
            // Note: We could optionally update the existing config with the new key
            credentialResolution = {
              source: "user",
              configId: existingConfig.uuid,
            };
          }
        }
        // Build advanced config object, only including defined values
        const advancedConfig: {
          temperature?: number;
          max_tokens?: number;
          top_p?: number;
          apiKey?: string;
        } = {};

        if (temperature !== "") {
          advancedConfig.temperature = Number(temperature);
        }
        if (maxTokens !== "") {
          advancedConfig.max_tokens = Number(maxTokens);
        }
        if (topP !== "") {
          advancedConfig.top_p = Number(topP);
        }

        // Include inline API key if user doesn't want to save it (use for this scan only)
        if (credentialResolution.source === "inline" && apiKey.trim() && !shouldSaveKey) {
          advancedConfig.apiKey = apiKey.trim();
        }

        // If using managed secret, auto-create a LLMProviderConfig linked to it
        if (credentialResolution.source === "managed" && credentialResolution.managedSecretId) {
          const existingManagedConfig = userProviderConfigs.find(
            (config) =>
              config.provider_type === providerType &&
              config.managed_secret_id === credentialResolution.managedSecretId &&
              config.is_active
          );

          if (!existingManagedConfig) {
            try {
              setIsCreatingProviderConfig(true);
              const newManagedConfig = await createProviderConfig({
                provider_type: providerType,
                managed_secret_id: credentialResolution.managedSecretId,
              });

              setUserProviderConfigs((prev) => [...prev, newManagedConfig]);

              credentialResolution = {
                source: "user",
                configId: newManagedConfig.uuid,
              };
            } catch (createError) {
              console.error("Failed to create provider config for managed secret:", createError);
              // Continue with managed secret ID - backend will handle it
            } finally {
              setIsCreatingProviderConfig(false);
            }
          } else {
            credentialResolution = {
              source: "user",
              configId: existingManagedConfig.uuid,
            };
          }
        }

        // Update config with foundation model target and credential references
        updatedConfig = updateConfigTarget(redteamConfig, "our-ai", {
          modelId: selectedModel as FoundationModelId,
          advancedConfig: Object.keys(advancedConfig).length > 0 ? advancedConfig : undefined,
          providerConfigId:
            credentialResolution.source === "user" ? credentialResolution.configId : undefined,
          managedSecretId:
            credentialResolution.source === "managed"
              ? credentialResolution.managedSecretId
              : undefined,
        });
        // Update prompts with user's prompt
        updatedConfig = updateConfigPrompts(updatedConfig, [prompt || "{{prompt}}"]);
      }

      setRedteamConfig(updatedConfig);
      setStep2Error(null);
      return true;
    } catch (error) {
      // Show user-friendly error message without exposing backend details
      let userMessage: string;
      if (agentType === "our-ai") {
        if (error instanceof Error) {
          // Check for specific error types to provide helpful guidance
          if (error.message.includes("Invalid model ID")) {
            userMessage = "Invalid model selection. Please select a valid model and try again.";
          } else {
            userMessage =
              `Unable to configure test parameters for ${selectedModel || "the selected model"}. ` +
              "Please check your settings and try again.";
          }
        } else {
          userMessage =
            `Unable to configure test parameters for ${selectedModel || "the selected model"}. ` +
            "Please check your settings and try again.";
        }
      } else {
        userMessage =
          "Unable to configure test parameters. Please check your endpoint settings and try again.";
      }

      setStep2Error(userMessage);

      return false;
    }
  };

  /**
   * Submit scan configuration to the backend API.
   * Extracted into a reusable helper to avoid code duplication.
   */
  const submitScan = async (configToSubmit: RedteamConfig): Promise<void> => {
    if (isQuotaExceeded) {
      setScanSubmitError(
        `Report limit reached (${quota?.used_reports ?? "?"}/${quota?.max_reports ?? "?"}). ` +
          `You currently have ${activeScans} scan(s) in progress. Finish an in-progress scan or upgrade your plan to run more.`
      );
      setIsSubmittingScan(false);
      setCurrentStep(4);
      return;
    }
    setIsSubmittingScan(true);
    setScanSubmitError(null);

    try {
      // Prepare config for submission
      const { configContent, providerConfigId } = prepareConfigForSubmission(configToSubmit);

      // Check if we have inline API key without save (temp credentials)
      let tempCredentials: { api_key?: string } | undefined;
      if (agentType === "our-ai" && apiKey.trim() && !saveApiKey) {
        const targets = configContent.targets as Array<{ config?: { apiKey?: string } }>;
        const target = targets?.[0]?.config;
        if (target?.apiKey) {
          tempCredentials = { api_key: target.apiKey };
        }
      }

      // Submit scan
      const scanData: LLMScanCreate = {
        config_content: configContent,
        ...(providerConfigId ? { provider_config_id: providerConfigId } : {}),
        ...(tempCredentials ? { temp_credentials: tempCredentials } : {}),
      };

      const createdScan = await createScan(scanData);
      setCreatedScanUuid(createdScan.uuid);

      // Refresh quota after successful submission
      refetchQuota();

      // Track successful scan submission
      track({
        name: "demo_flow_scan_submitted",
        properties: {
          plugins_count: selectedPlugins.length,
          strategies_count: selectedStrategies.length,
        },
      });

      // Clear persisted form data after successful submission (but keep step at 5)
      // We want to stay on Step 5 to show the success message
      clearRedteamConfig();
      clearAgentType();
      clearApiEndpoint();
      clearMethod();
      clearHeaders();
      clearRequestBody();
      clearSelectedPreset();
      clearSelectedPlugins();
      clearPluginConfigurations();
      clearSelectedStrategyPreset();
      clearSelectedStrategies();
      clearSelectedModel();
      clearPrompt();
      clearTemperature();
      clearMaxTokens();
      clearTopP();
      setApiKey("");
      clearSaveApiKey();
      // Don't clear currentStep - we want to stay on Step 5 to show success message
    } catch (error) {
      console.error("Failed to submit scan:", error);
      let errorMessage = "Failed to submit scan. Please try again.";
      if (error instanceof Error) {
        // Provide user-friendly error messages
        if (
          error.message.toLowerCase().includes("report limit") ||
          error.message.toLowerCase().includes("scan in progress")
        ) {
          errorMessage = error.message;
        } else if (error.message.includes("credentials")) {
          errorMessage = "No API credentials found. Please configure your API key and try again.";
        } else if (error.message.includes("provider")) {
          errorMessage =
            "Invalid provider configuration. Please check your settings and try again.";
        }
      }
      setScanSubmitError(errorMessage);
    } finally {
      setIsSubmittingScan(false);
    }
  };

  const handleNext = async () => {
    // Defense-in-depth: block moving forward when quota is exhausted even if a button is somehow triggered.
    if (isQuotaExceeded) {
      return;
    }
    if (currentStep === 1 && canProceedFromStep1) {
      setCurrentStep(2);
      track({
        name: "demo_flow_step_completed",
        properties: { step: 1, agent_type: agentType || undefined },
      });
    } else if (currentStep === 2) {
      if (
        (agentType === "own" && canProceedFromStep2a) ||
        (agentType === "our-ai" && canProceedFromStep2b)
      ) {
        // Update config before moving to next step (async for provider config creation)
        const success = await handleStep2Complete();
        if (success) {
          setCurrentStep(3);
          track({
            name: "demo_flow_step_completed",
            properties: { step: 2, agent_type: agentType || undefined },
          });
        }
      }
    } else if (currentStep === 3 && canProceedFromStep3) {
      // Update config with selected plugins before moving to next step
      const updatedConfig = updateConfigPlugins(
        redteamConfig,
        selectedPlugins,
        pluginConfigurations,
        selectedPreset || undefined
      );
      setRedteamConfig(updatedConfig);
      setCurrentStep(4);
      track({
        name: "demo_flow_step_completed",
        properties: { step: 3, agent_type: agentType || undefined },
      });
    } else if (currentStep === 4 && canProceedFromStep4) {
      // Update config with selected strategies before submitting
      const updatedConfig = updateConfigStrategies(redteamConfig, selectedStrategies);
      setRedteamConfig(updatedConfig);

      // Transition to Step 5 and submit scan
      setCurrentStep(5);
      track({
        name: "demo_flow_step_completed",
        properties: { step: 4, agent_type: agentType || undefined },
      });
      await submitScan(updatedConfig);
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else if (currentStep === 3) {
      setCurrentStep(2);
    } else if (currentStep === 4) {
      setCurrentStep(3);
    } else if (currentStep === 5) {
      setCurrentStep(4);
    }
  };

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 max-w-4xl mx-auto">
      {/* Progress indicator */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2 flex-1">
          <div className={`h-2 flex-1 rounded ${currentStep >= 1 ? "bg-primary" : "bg-muted"}`} />
          <div className={`h-2 flex-1 rounded ${currentStep >= 2 ? "bg-primary" : "bg-muted"}`} />
          <div className={`h-2 flex-1 rounded ${currentStep >= 3 ? "bg-primary" : "bg-muted"}`} />
          <div className={`h-2 flex-1 rounded ${currentStep >= 4 ? "bg-primary" : "bg-muted"}`} />
          <div className={`h-2 flex-1 rounded ${currentStep >= 5 ? "bg-primary" : "bg-muted"}`} />
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            restartTour().catch((error) => {
              console.error("Failed to restart scan wizard tour:", error);
            });
          }}
          className="flex items-center gap-2"
          title="Show tour guide"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="hidden sm:inline">Help</span>
        </Button>
      </div>

      {/* Global quota gate (all steps) */}
      {!quotaLoading && isQuotaExceeded && (
        <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-medium text-sm text-destructive">Report Limit Reached</div>
            <div className="text-sm text-destructive/90 mt-1">
              <>
                You&apos;ve used {quota.used_reports}/{quota.max_reports ?? "unlimited"} reports and
                have {activeScans} scan(s) in progress.{" "}
              </>
              <a
                href="mailto:support@earlycore.dev?subject=Increase Report Limit"
                className="underline hover:text-destructive"
              >
                Contact support
              </a>
              .
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Agent Selection */}
      {currentStep === 1 && (
        <Card data-tour="connect-step">
          <CardHeader>
            <CardTitle>Ready to generate your report</CardTitle>
            <CardDescription>Select how you want to generate your report?</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Clone Loading Banner */}
            {cloneLoading && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <Loader2 className="h-5 w-5 text-blue-600 animate-spin shrink-0" />
                <div className="text-sm text-blue-900">
                  Loading configuration from shared report...
                </div>
              </div>
            )}
            {/* Clone Error Banner */}
            {cloneError && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-destructive">{cloneError}</div>
                </div>
              </div>
            )}
            {/* Pre-filled Config Banner */}
            {isConfigCloned && !cloneLoading && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-blue-900">
                    Pre-filled from the report you viewed.
                  </div>
                </div>
              </div>
            )}
            <RadioGroup
              value={agentType}
              onValueChange={(value) => setAgentType(value as AgentType)}
            >
              <RadioGroupItem
                value="our-ai"
                label="Quick Scan (recommended)"
                description="Use our pre-configured AI models for a quick scan"
              />
              <RadioGroupItem
                value="own"
                label="Connect my agent endpoint"
                description="Connect your AI agent via API endpoint"
              />
            </RadioGroup>

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleNext}
                disabled={!canProceedFromStep1 || isQuotaExceeded || quotaLoading}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2a: Own Agent Configuration */}
      {currentStep === 2 && agentType === "own" && (
        <Card data-tour="own-agent-config">
          <CardHeader>
            <CardTitle>Configure Your Agent</CardTitle>
            <CardDescription>
              Provide your agent's API details and select tests to run
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-orange-50 dark:bg-orange-950/30 border border-orange-200 dark:border-orange-800 rounded-md p-4 space-y-2">
              <p className="text-sm font-medium text-orange-800 dark:text-orange-200">
                ⚠️ Development Environment Only
              </p>
              <p className="text-xs text-orange-700 dark:text-orange-300">
                This feature should only be used with <strong>development/staging endpoints</strong>
                . Production APIs may block requests due to CORS restrictions, rate limiting, or
                firewall rules. If you're seeing 405 errors, your API may not accept POST requests
                from external origins.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="api-endpoint">API Endpoint</Label>
              <p className="text-xs text-muted-foreground">
                Enter the <strong>API endpoint URL</strong> (not the chat page URL). Example:{" "}
                <code className="bg-muted px-1 rounded">
                  https://api.example.com/v1/chat/completions
                </code>
              </p>
              <Input
                id="api-endpoint"
                placeholder="https://api.example.com/v1/chat/completions"
                value={apiEndpoint}
                onChange={(e) => setApiEndpoint(e.target.value)}
              />
              {apiEndpoint.trim() !== "" && getUrlError(apiEndpoint) && (
                <p className="text-sm text-destructive">{getUrlError(apiEndpoint)}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="method">Method</Label>
              <Select value={method} onValueChange={(value) => setMethod(value as HttpMethod)}>
                <SelectTrigger id="method">
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {HTTP_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>
                      {m}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Headers</Label>
                  <button
                    type="button"
                    onClick={() => setIsHeadersInfoExpanded(!isHeadersInfoExpanded)}
                    className="inline-flex items-center justify-center rounded-full p-1 hover:bg-muted transition-colors"
                    aria-label="Toggle headers information"
                    aria-expanded={isHeadersInfoExpanded}
                  >
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddHeader}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Header
                </Button>
              </div>
              {isHeadersInfoExpanded && (
                <div className="space-y-3 text-xs text-muted-foreground bg-muted/50 p-3 rounded-md animate-in slide-in-from-top-2 duration-200">
                  <p className="font-medium text-foreground">How do headers work?</p>
                  <p>
                    Headers are sent with every request to your API endpoint. The most important
                    header for authentication is <strong>Authorization</strong>.
                  </p>

                  <div className="bg-background border rounded p-2 space-y-1">
                    <p className="font-medium text-foreground text-xs">
                      Correct Authorization Header Format:
                    </p>
                    <div className="font-mono text-xs">
                      <span className="text-muted-foreground">Name:</span>{" "}
                      <code className="bg-primary/10 text-primary px-1 rounded">Authorization</code>
                    </div>
                    <div className="font-mono text-xs">
                      <span className="text-muted-foreground">Value:</span>{" "}
                      <code className="bg-primary/10 text-primary px-1 rounded">
                        Bearer eyJhbGciOiJS...
                      </code>
                    </div>
                  </div>

                  <div className="bg-destructive/10 border border-destructive/20 rounded p-2 space-y-1">
                    <p className="font-medium text-destructive text-xs">
                      Common Mistake - Don't do this:
                    </p>
                    <div className="font-mono text-xs">
                      <span className="text-muted-foreground">Name:</span>{" "}
                      <code className="bg-destructive/20 text-destructive px-1 rounded">
                        Bearer
                      </code>
                    </div>
                    <div className="font-mono text-xs">
                      <span className="text-muted-foreground">Value:</span>{" "}
                      <code className="bg-destructive/20 text-destructive px-1 rounded">
                        eyJhbGciOiJS...
                      </code>
                    </div>
                    <p className="text-destructive/80 text-xs mt-1">
                      "Bearer" is part of the <em>value</em>, not the header name.
                    </p>
                  </div>

                  <p className="text-muted-foreground/80">
                    <strong>Other common headers:</strong> Content-Type (usually{" "}
                    <code className="bg-muted px-1 rounded">application/json</code>), custom API
                    keys, etc.
                  </p>
                </div>
              )}
              <div className="space-y-2">
                {headers.map((header) => (
                  <div key={header.id} className="flex items-center gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Name"
                        value={header.name}
                        onChange={(e) => handleHeaderChange(header.id, "name", e.target.value)}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) => handleHeaderChange(header.id, "value", e.target.value)}
                        data-posthog-mask
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveHeader(header.id)}
                      disabled={headers.length === 1}
                      className="flex-shrink-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="request-body">Request Body</Label>
                <button
                  type="button"
                  onClick={() => setIsRequestBodyInfoExpanded(!isRequestBodyInfoExpanded)}
                  className="inline-flex items-center justify-center rounded-full p-1 hover:bg-muted transition-colors"
                  aria-label="Toggle request body information"
                  aria-expanded={isRequestBodyInfoExpanded}
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <Textarea
                id="request-body"
                placeholder='{"message": "{{prompt}}"}'
                value={requestBody}
                onChange={(e) => setRequestBody(e.target.value)}
                className="min-h-[150px] font-mono text-sm"
              />
              {isRequestBodyInfoExpanded && (
                <div className="space-y-2 text-xs text-muted-foreground bg-muted/50 p-3 rounded-md animate-in slide-in-from-top-2 duration-200">
                  <p className="font-medium text-foreground">How does the request body work?</p>
                  <p>
                    The request body is the JSON payload sent to your API endpoint. The{" "}
                    <code className="bg-muted px-1 rounded">{"{{prompt}}"}</code> placeholder marks
                    where attack prompts will be injected during testing.
                  </p>
                  <p>
                    <strong>Required:</strong> Your request body must include{" "}
                    <code className="bg-muted px-1 rounded">{"{{prompt}}"}</code> somewhere in the
                    JSON structure. This is where EarlyCore will inject the security test prompts.
                  </p>
                  <p className="text-muted-foreground/80">
                    <strong>Example:</strong>
                  </p>
                  <pre className="bg-muted p-2 rounded text-xs overflow-x-auto">
                    {`{
  "message": "{{prompt}}",
  "conversation_id": "your-conversation-id",
  "project_id": "37"
}`}
                  </pre>
                </div>
              )}
              {requestBody.trim() !== "" && !hasPromptPlaceholder(requestBody) && (
                <p className="text-sm text-destructive">
                  Request body must contain {"{{prompt}}"}. This is where EarlyCore will inject the
                  attack payload.
                </p>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceedFromStep2a || isQuotaExceeded || quotaLoading}
              >
                Proceed
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2b: Our AI Configuration */}
      {currentStep === 2 && agentType === "our-ai" && (
        <Card>
          <CardHeader>
            <CardTitle>Configure Test Parameters</CardTitle>
            <CardDescription>Select a model and provide a prompt to test</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6" data-tour="provider-cards">
            {step2Error && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1">
                  <div className="font-medium text-sm text-destructive">Configuration Error</div>
                  <div className="text-sm text-destructive/90">{step2Error}</div>
                </div>
                <button
                  type="button"
                  onClick={() => setStep2Error(null)}
                  className="text-destructive hover:text-destructive/80 shrink-0"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {credentialsLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading provider credentials...
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="model-select">Select which model to test on</Label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger id="model-select">
                  <SelectValue placeholder="Select a model" />
                </SelectTrigger>
                <SelectContent>
                  {FOUNDATION_MODELS.map((model) => (
                    <SelectItem key={model.value} value={model.value}>
                      {model.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedModel && (
                <p className="text-xs text-muted-foreground">
                  {FOUNDATION_MODELS.find((m) => m.value === selectedModel)?.description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="prompt">Prompt Template</Label>
                <button
                  type="button"
                  onClick={() => setIsPromptInfoExpanded(!isPromptInfoExpanded)}
                  className="inline-flex items-center justify-center rounded-full p-1 hover:bg-muted transition-colors"
                  aria-label="Toggle prompt information"
                  aria-expanded={isPromptInfoExpanded}
                >
                  <Info className="h-4 w-4 text-muted-foreground" />
                </button>
              </div>
              <Textarea
                id="prompt"
                placeholder="You are a helpful assistant...

User: {{prompt}}"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="min-h-[200px]"
              />
              {isPromptInfoExpanded && (
                <div className="space-y-2 text-xs text-muted-foreground bg-muted/50 p-3 rounded-md animate-in slide-in-from-top-2 duration-200">
                  <p className="font-medium text-foreground">How does this work?</p>
                  <p>
                    This is the <strong>system prompt or instruction</strong> for your AI. The{" "}
                    <code className="bg-muted px-1 rounded">{"{{prompt}}"}</code> placeholder marks
                    where attack prompts will be injected during testing.
                  </p>
                  <p>
                    <strong>Tip:</strong> For more realistic results, paste your actual system
                    prompt here. This tests whether attackers can bypass <em>your specific</em>{" "}
                    instructions.
                  </p>
                  <p className="text-muted-foreground/80">
                    Example: "You are a customer service bot for Acme Corp. Be helpful but never
                    share customer data. User: {"{{prompt}}"}"
                  </p>
                </div>
              )}
            </div>

            {/* Advanced Configuration */}
            {(() => {
              const needsApiKey = selectedModel && requiresApiKey(selectedModel);
              const hasCredentials = selectedModel
                ? hasCredentialsForProvider(
                    extractProviderFromModelId(selectedModel) || "",
                    apiKey,
                    userProviderConfigs,
                    managedSecrets
                  )
                : false;
              const showApiKeyIndicator = needsApiKey && !hasCredentials && apiKey.trim() === "";

              const collapsibleTitle = (
                <div className="flex items-center gap-2">
                  <span>Advanced Configuration</span>
                  {showApiKeyIndicator && (
                    <span
                      className="h-2 w-2 rounded-full bg-destructive"
                      aria-label="API key required"
                      title="API key required"
                    />
                  )}
                </div>
              );

              return (
                <Collapsible
                  title={collapsibleTitle}
                  description="Model parameters and API settings (optional)"
                  className="border-t pt-4"
                  defaultOpen={
                    showApiKeyIndicator ||
                    apiKey.trim() !== "" ||
                    temperature !== "" ||
                    maxTokens !== "" ||
                    topP !== ""
                  }
                >
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="temperature">Temperature</Label>
                      <Input
                        id="temperature"
                        type="number"
                        min="0"
                        max="2"
                        step="0.1"
                        placeholder="0.7"
                        value={temperature}
                        onChange={(e) =>
                          setTemperature(e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Controls randomness (0.0 to 2.0). Lower values make output more
                        deterministic.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="max-tokens">Max Tokens</Label>
                      <Input
                        id="max-tokens"
                        type="number"
                        min="1"
                        placeholder="2048"
                        value={maxTokens}
                        onChange={(e) =>
                          setMaxTokens(e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Maximum number of tokens to generate in the response.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="top-p">Top P</Label>
                      <Input
                        id="top-p"
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        placeholder="1.0"
                        value={topP}
                        onChange={(e) =>
                          setTopP(e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                      <p className="text-xs text-muted-foreground">
                        Nucleus sampling parameter (0.0 to 1.0). Controls diversity of output.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="api-key">
                        API Key
                        {selectedModel && requiresApiKey(selectedModel) && (
                          <span className="text-destructive ml-1">*</span>
                        )}
                      </Label>
                      <Input
                        id="api-key"
                        type="password"
                        placeholder={
                          selectedModel && requiresApiKey(selectedModel)
                            ? "Required for Anthropic models"
                            : "Optional - uses environment variable by default"
                        }
                        value={apiKey}
                        onChange={(e) => setApiKey(e.target.value)}
                        data-posthog-mask
                        required={selectedModel ? requiresApiKey(selectedModel) : false}
                      />
                      <p className="text-xs text-muted-foreground">
                        {selectedModel && requiresApiKey(selectedModel) ? (
                          <>
                            <span className="text-destructive font-medium">Required</span> for
                            Anthropic models. Please provide your API key or ensure you have a saved
                            provider configuration.
                          </>
                        ) : (
                          <>
                            Optional API key. If not provided, will use the environment variable for
                            the selected provider.
                          </>
                        )}
                      </p>
                      {apiKey.trim() && (
                        <div className="flex items-center space-x-2 pt-2">
                          <Checkbox
                            id="save-api-key"
                            checked={saveApiKey}
                            onCheckedChange={(checked) => setSaveApiKey(checked === true)}
                          />
                          <Label
                            htmlFor="save-api-key"
                            className="text-sm font-normal cursor-pointer"
                          >
                            Save this API key for future scans
                          </Label>
                        </div>
                      )}
                    </div>
                  </div>
                </Collapsible>
              );
            })()}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceedFromStep2b || isCreatingProviderConfig}
              >
                {isCreatingProviderConfig ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    Proceed
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Select Plugins and Configure */}
      {currentStep === 3 && (
        <>
          <Card data-tour="configure-plugins-step">
            <CardHeader>
              <CardTitle>Select Plugins to Run</CardTitle>
              <CardDescription>
                Choose a compliance framework or select plugins manually to configure your security
                tests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Compliance Frameworks Section */}
              <div className="space-y-4">
                <Label>Compliance Frameworks</Label>
                <RadioGroup
                  value={selectedPreset}
                  onValueChange={handleFrameworkChange}
                  className="space-y-2"
                >
                  {PLUGIN_PRESETS.map((framework) => (
                    <div key={framework.id}>
                      <RadioGroupItem
                        value={framework.id}
                        label={framework.name}
                        description={
                          framework.requiresPayment && !framework.available ? (
                            <>
                              {framework.description}{" "}
                              <span className="text-orange-600 font-medium">
                                (Requires subscription)
                              </span>
                            </>
                          ) : (
                            framework.description
                          )
                        }
                        disabled={!framework.available}
                      />
                    </div>
                  ))}
                </RadioGroup>
              </div>

              {/* Plugins Section */}
              <div className="space-y-4">
                <Label>Plugins</Label>
                <TooltipProvider>
                  <div className="space-y-2">
                    {ALL_PLUGINS.map((pluginId) => {
                      const isSelected = selectedPlugins.includes(pluginId);
                      const isConfigured = isPluginConfigured(pluginId);
                      const requiresConfig = pluginRequiresConfiguration(pluginId);
                      // Only show red indicator if plugin is selected AND requires config but isn't configured
                      const needsConfiguration = isSelected && requiresConfig && !isConfigured;
                      const isDisabled = !!selectedPreset;
                      const tooltipId = `plugin-tooltip-${pluginId}`;

                      return (
                        <div
                          key={pluginId}
                          className={`flex items-start space-x-3 p-4 rounded-md border ${
                            needsConfiguration
                              ? "border-destructive bg-destructive/5"
                              : "border-input bg-background"
                          }`}
                        >
                          {isDisabled ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div>
                                  <Checkbox
                                    id={pluginId}
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    className="mt-1"
                                    aria-disabled={true}
                                    aria-describedby={tooltipId}
                                  />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent id={tooltipId}>
                                <p>Plugins are controlled by the selected compliance framework</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Checkbox
                              id={pluginId}
                              checked={isSelected}
                              disabled={false}
                              onCheckedChange={() => handlePluginToggle(pluginId)}
                              className="mt-1"
                            />
                          )}
                          <div className="flex-1">
                            <label
                              htmlFor={pluginId}
                              className="cursor-default flex items-start justify-between"
                            >
                              <div className="flex-1">
                                <div className="font-medium flex items-center gap-2">
                                  {getPluginDisplayName(pluginId)}
                                  {needsConfiguration && (
                                    <span className="text-xs text-destructive font-normal">
                                      (Configuration Required)
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                  {getPluginDescription(pluginId)}
                                </div>
                              </div>
                            </label>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenPluginConfig(pluginId)}
                            className="flex-shrink-0"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </TooltipProvider>
              </div>

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={handleBack}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleNext}
                  disabled={!canProceedFromStep3 || isQuotaExceeded || quotaLoading}
                >
                  Proceed
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Plugin Configuration Dialog */}
          <Dialog open={configuringPluginId !== null} onOpenChange={handleClosePluginConfig}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {configuringPluginId
                    ? getPluginDisplayName(configuringPluginId)
                    : "Plugin Configuration"}
                </DialogTitle>
                <DialogDescription>
                  Configure this plugin's settings and grading criteria
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4 px-3 max-h-[60vh] overflow-y-auto">
                {configuringPluginId && (
                  <>
                    {getPluginConfigSchema(configuringPluginId).fields.map((field) => {
                      // Skip graderGuidance here, we'll handle it separately at the end
                      if (field.key === "graderGuidance") return null;

                      if (field.type === "array") {
                        // Special handling for policy plugin - use reusable component
                        if (configuringPluginId === "policy" && field.key === "policies") {
                          const existingPolicies = Array.isArray(tempConfig[field.key])
                            ? (tempConfig[field.key] as PolicyObject[])
                            : [];

                          return (
                            <PolicyConfiguration
                              key={field.key}
                              policies={existingPolicies}
                              onChange={(updated) => {
                                setTempConfig((prev) => ({
                                  ...prev,
                                  [field.key]: updated,
                                }));
                              }}
                              required={field.required}
                              description={field.description}
                              label={field.label}
                            />
                          );
                        }

                        // Regular array field (e.g., intent, string arrays)
                        const generateArrayItemId = () =>
                          `item-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

                        type ArrayItem = { id: string; value: string };
                        const normalizeArray = (arr: unknown): ArrayItem[] => {
                          if (!Array.isArray(arr)) {
                            return [{ id: generateArrayItemId(), value: "" }];
                          }
                          if (configuringPluginId === "policy" && field.key === "policies") {
                            return [];
                          }
                          if (
                            arr.length > 0 &&
                            typeof arr[0] === "object" &&
                            arr[0] !== null &&
                            "id" in arr[0] &&
                            "value" in arr[0]
                          ) {
                            return arr as ArrayItem[];
                          }
                          return (arr as unknown as string[]).map((value) => ({
                            id: generateArrayItemId(),
                            value: value || "",
                          }));
                        };

                        const arrayValue = normalizeArray(tempConfig[field.key]);

                        return (
                          <div key={field.key} className="space-y-2">
                            <Label htmlFor={field.key}>
                              {field.label}
                              {field.required && <span className="text-destructive ml-1">*</span>}
                            </Label>
                            {field.description && (
                              <p className="text-xs text-muted-foreground">{field.description}</p>
                            )}
                            <div className="space-y-2">
                              {arrayValue.map((item) => (
                                <div key={item.id} className="flex items-center gap-2">
                                  <Input
                                    id={`${field.key}-${item.id}`}
                                    placeholder={field.placeholder}
                                    value={item.value}
                                    className="flex-1"
                                    onChange={(e) => {
                                      const newArray = arrayValue.map((i) =>
                                        i.id === item.id ? { ...i, value: e.target.value } : i
                                      );
                                      setTempConfig((prev) => ({
                                        ...prev,
                                        [field.key]: newArray as unknown as string[],
                                      }));
                                    }}
                                  />
                                  {arrayValue.length > 1 && (
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        const newArray = arrayValue.filter((i) => i.id !== item.id);
                                        setTempConfig((prev) => ({
                                          ...prev,
                                          [field.key]: (newArray.length > 0
                                            ? newArray
                                            : [
                                                { id: generateArrayItemId(), value: "" },
                                              ]) as unknown as string[],
                                        }));
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTempConfig((prev) => ({
                                    ...prev,
                                    [field.key]: [
                                      ...arrayValue,
                                      { id: generateArrayItemId(), value: "" },
                                    ] as unknown as string[],
                                  }));
                                }}
                                disabled={arrayValue.some((item) => item.value.trim() === "")}
                                className="gap-2"
                              >
                                <Plus className="h-4 w-4" />
                                Add
                              </Button>
                            </div>
                          </div>
                        );
                      }

                      // Regular text or textarea field
                      const FieldComponent = field.type === "textarea" ? Textarea : Input;
                      return (
                        <div key={field.key} className="space-y-2">
                          <Label htmlFor={field.key}>
                            {field.label}
                            {field.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          {field.description && (
                            <p className="text-xs text-muted-foreground">{field.description}</p>
                          )}
                          <FieldComponent
                            id={field.key}
                            type={field.type === "text" ? "text" : undefined}
                            placeholder={field.placeholder}
                            value={(tempConfig[field.key] as string) || ""}
                            onChange={(e) =>
                              setTempConfig((prev) => ({
                                ...prev,
                                [field.key]: e.target.value,
                              }))
                            }
                            className={field.type === "textarea" ? "min-h-[120px]" : "w-full"}
                          />
                        </div>
                      );
                    })}

                    {/* Grading Guidance - always available, shown last */}
                    {getPluginConfigSchema(configuringPluginId)
                      .fields.filter((f) => f.key === "graderGuidance")
                      .map((field) => (
                        <div key={field.key} className="space-y-2">
                          <Label htmlFor={field.key}>
                            {field.label}
                            {field.required && <span className="text-destructive ml-1">*</span>}
                          </Label>
                          {field.description && (
                            <p className="text-xs text-muted-foreground">{field.description}</p>
                          )}
                          <Textarea
                            id={field.key}
                            placeholder={field.placeholder}
                            value={(tempConfig[field.key] as string) || ""}
                            onChange={(e) =>
                              setTempConfig((prev) => ({
                                ...prev,
                                [field.key]: e.target.value,
                              }))
                            }
                            className="min-h-[120px] w-full"
                          />
                        </div>
                      ))}
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={handleClosePluginConfig}>
                  Cancel
                </Button>
                <Button onClick={handleSavePluginConfig}>Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {/* Step 4: Strategies */}
      {currentStep === 4 && (
        <Card data-tour="configure-strategies-step">
          <CardHeader>
            <CardTitle>Strategies</CardTitle>
            <CardDescription>Configure your testing strategies</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Quota Exceeded Banner */}
            {!quotaLoading && isQuotaExceeded && (
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="font-medium text-sm text-destructive">Report Limit Reached</div>
                  <div className="text-sm text-destructive/90 mt-1">
                    You've used all {quota.max_reports} reports in your free tier.{" "}
                    <a
                      href="mailto:support@earlycore.dev?subject=Increase Report Limit"
                      className="underline hover:text-destructive"
                    >
                      Contact us to continue
                    </a>
                    .
                  </div>
                </div>
              </div>
            )}
            {/* Presets Section */}
            <div className="space-y-4">
              <Label>Presets</Label>
              <RadioGroup
                value={selectedStrategyPreset}
                onValueChange={handleStrategyPresetChange}
                className="space-y-2"
              >
                {STRATEGY_PRESETS.map((preset) => (
                  <div key={preset.id}>
                    <RadioGroupItem
                      value={preset.id}
                      label={preset.name}
                      description={
                        preset.requiresPayment && !preset.available ? (
                          <>
                            {preset.description}{" "}
                            <span className="text-orange-600 font-medium">
                              (Requires subscription)
                            </span>
                          </>
                        ) : (
                          preset.description
                        )
                      }
                      disabled={!preset.available}
                    />
                  </div>
                ))}
              </RadioGroup>
            </div>

            {/* Recommended Strategies Section */}
            <div className="space-y-4">
              <div>
                <Label>Recommended Strategies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Core strategies that provide comprehensive coverage for most use cases
                </p>
              </div>
              <div className="space-y-2">
                {STRATEGIES.filter((s) => s.category === "recommended").map((strategy) => {
                  const isSelected = selectedStrategies.includes(strategy.id);
                  const isLocked = strategy.locked && strategy.requiresPayment;

                  return (
                    <div
                      key={strategy.id}
                      className={`flex items-start space-x-3 p-4 rounded-md border ${
                        isLocked
                          ? "border-muted bg-muted/50 opacity-60"
                          : "border-input bg-background"
                      }`}
                    >
                      <Checkbox
                        id={strategy.id}
                        checked={isSelected}
                        onCheckedChange={() => handleStrategyToggle(strategy.id)}
                        disabled={isLocked}
                        className="mt-1"
                      />
                      <label
                        htmlFor={strategy.id}
                        className={`flex-1 cursor-pointer ${isLocked ? "cursor-not-allowed" : ""}`}
                      >
                        <div className="font-medium flex items-center gap-2">
                          {strategy.name}
                          {isLocked && (
                            <span className="text-xs text-orange-600 font-normal">
                              (Requires subscription)
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {strategy.description}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Agentic Strategies Section */}
            <div className="space-y-4">
              <div>
                <Label>Agentic Strategies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Advanced AI-powered strategies that dynamically adapt their attack patterns
                </p>
              </div>

              {/* Single-turn Only */}
              <div className="space-y-2 pl-4 border-l-2">
                <Label className="text-sm font-medium">Single-turn Only</Label>
                <p className="text-xs text-muted-foreground">
                  These strategies work only for single-turn evaluations.
                </p>
                <div className="space-y-2 mt-2">
                  {STRATEGIES.filter((s) => s.category === "agentic-single").map((strategy) => {
                    const isSelected = selectedStrategies.includes(strategy.id);
                    const isLocked = strategy.locked && strategy.requiresPayment;

                    return (
                      <div
                        key={strategy.id}
                        className={`flex items-start space-x-3 p-4 rounded-md border ${
                          isLocked
                            ? "border-muted bg-muted/50 opacity-60"
                            : "border-input bg-background"
                        }`}
                      >
                        <Checkbox
                          id={strategy.id}
                          checked={isSelected}
                          onCheckedChange={() => handleStrategyToggle(strategy.id)}
                          disabled={isLocked}
                          className="mt-1"
                        />
                        <label
                          htmlFor={strategy.id}
                          className={`flex-1 cursor-pointer ${isLocked ? "cursor-not-allowed" : ""}`}
                        >
                          <div className="font-medium flex items-center gap-2">
                            {strategy.name}
                            {isLocked && (
                              <span className="text-xs text-orange-600 font-normal">
                                (Requires subscription)
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {strategy.description}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Single and Multi-turn */}
              <div className="space-y-2 pl-4 border-l-2">
                <Label className="text-sm font-medium">Single and Multi-turn</Label>
                <p className="text-xs text-muted-foreground">
                  These strategies can be used for both single and multi-turn evaluations.
                </p>
                <div className="space-y-2 mt-2">
                  {STRATEGIES.filter((s) => s.category === "agentic-multi").map((strategy) => {
                    const isSelected = selectedStrategies.includes(strategy.id);
                    const isLocked = strategy.locked && strategy.requiresPayment;

                    return (
                      <div
                        key={strategy.id}
                        className={`flex items-start space-x-3 p-4 rounded-md border ${
                          isLocked
                            ? "border-muted bg-muted/50 opacity-60"
                            : "border-input bg-background"
                        }`}
                      >
                        <Checkbox
                          id={strategy.id}
                          checked={isSelected}
                          onCheckedChange={() => handleStrategyToggle(strategy.id)}
                          disabled={isLocked}
                          className="mt-1"
                        />
                        <label
                          htmlFor={strategy.id}
                          className={`flex-1 cursor-pointer ${isLocked ? "cursor-not-allowed" : ""}`}
                        >
                          <div className="font-medium flex items-center gap-2">
                            {strategy.name}
                            {isLocked && (
                              <span className="text-xs text-orange-600 font-normal">
                                (Requires subscription)
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {strategy.description}
                          </div>
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Multi-modal Strategies Section */}
            <div className="space-y-4">
              <div>
                <Label>Multi-modal Strategies</Label>
                <p className="text-sm text-muted-foreground mt-1">
                  Test handling of non-text content including audio, video, and images
                </p>
              </div>
              <div className="space-y-2">
                {STRATEGIES.filter((s) => s.category === "multimodal").map((strategy) => {
                  const isSelected = selectedStrategies.includes(strategy.id);
                  const isLocked = strategy.locked && strategy.requiresPayment;

                  return (
                    <div
                      key={strategy.id}
                      className={`flex items-start space-x-3 p-4 rounded-md border ${
                        isLocked
                          ? "border-muted bg-muted/50 opacity-60"
                          : "border-input bg-background"
                      }`}
                    >
                      <Checkbox
                        id={strategy.id}
                        checked={isSelected}
                        onCheckedChange={() => handleStrategyToggle(strategy.id)}
                        disabled={isLocked}
                        className="mt-1"
                      />
                      <label
                        htmlFor={strategy.id}
                        className={`flex-1 cursor-pointer ${isLocked ? "cursor-not-allowed" : ""}`}
                      >
                        <div className="font-medium flex items-center gap-2">
                          {strategy.name}
                          {isLocked && (
                            <span className="text-xs text-orange-600 font-normal">
                              (Requires subscription)
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {strategy.description}
                        </div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={!canProceedFromStep4 || isQuotaExceeded || quotaLoading}
                data-tour="run-scan-button"
              >
                Proceed
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 5: Submission Status */}
      {currentStep === 5 && (
        <Card>
          <CardHeader>
            <CardTitle>
              {isSubmittingScan
                ? "Submitting Scan"
                : scanSubmitError
                  ? "Submission Failed"
                  : "Scan Scheduled"}
            </CardTitle>
            <CardDescription>
              {isSubmittingScan
                ? "Your compliance scan is being submitted..."
                : scanSubmitError
                  ? "There was an error submitting your scan"
                  : "Your compliance scan has been scheduled and will begin shortly."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {isSubmittingScan ? (
              <div className="text-center py-12 space-y-6">
                <div className="flex justify-center">
                  <Loader2 className="h-16 w-16 text-primary animate-spin" />
                </div>
                <div className="space-y-3">
                  <p className="text-lg font-medium">Submitting your scan configuration...</p>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    Please wait while we process your request.
                  </p>
                </div>
              </div>
            ) : scanSubmitError ? (
              <div className="text-center py-12 space-y-6">
                <div className="flex justify-center">
                  <AlertCircle className="h-16 w-16 text-destructive" />
                </div>
                <div className="space-y-3">
                  <p className="text-lg font-medium text-destructive">Submission Failed</p>
                  <p className="text-muted-foreground max-w-md mx-auto">{scanSubmitError}</p>
                </div>
                <div className="pt-4 flex gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setScanSubmitError(null);
                      setCurrentStep(4);
                    }}
                  >
                    Go Back
                  </Button>
                  <Button
                    onClick={async () => {
                      // Retry submission
                      await submitScan(redteamConfig);
                    }}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 space-y-6">
                <div className="flex justify-center">
                  <Clock className="h-16 w-16 text-muted-foreground" />
                </div>
                <div className="space-y-3">
                  <p className="text-lg font-medium">
                    Your compliance scan has been scheduled successfully.
                  </p>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    You'll receive a notification when your scan results are ready. You can also
                    monitor the Scans page to check the status of your scan. Please note that
                    processing may take some time, depending on the scope of your scan.
                  </p>
                </div>
                <div className="pt-4 flex gap-3 justify-center">
                  {createdScanUuid && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate(`/llm-scans/${createdScanUuid}/results`);
                      }}
                    >
                      View Results
                    </Button>
                  )}
                  <Button
                    variant="secondary"
                    onClick={() => {
                      clearAllPersistedState();
                      setCurrentStep(1);
                      setCreatedScanUuid(null);
                      setScanSubmitError(null);
                    }}
                  >
                    Start New Scan
                  </Button>
                </div>
              </div>
            )}

            {/* Development-only YAML Preview */}
            {process.env.NODE_ENV === "development" && (
              <Card className="border-dashed border-2">
                <CardHeader>
                  <CardTitle className="text-sm">Generated YAML Configuration (Dev Only)</CardTitle>
                  <CardDescription className="text-xs">
                    This is the YAML that will be sent to the backend API
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <pre className="text-xs overflow-auto max-h-96 p-4 bg-slate-900 text-slate-100 rounded border border-slate-700 font-mono">
                      <code>{generatedYaml}</code>
                    </pre>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
